import type { TestRunVersionProgress } from "src/api/endpoints";
import {
  type LiveStatusDetail,
  LiveStatusDetailKind,
  type ToolMessage,
} from "src/clients/agent-management";

/**
 * A dataset test run the runner is currently waiting on, as announced through
 * the session's live status while the run-reading tool call is still open.
 */
export type PolledRun = {
  testRunId: string;
  datasetId: string | null;
  flowVersionId: string | null;
  /** First-paint counts; the run API is the live source once polling starts. */
  progress: TestRunVersionProgress | null;
};

/** The run tool result's artifact, describing a settled or handed-off run. */
export type TestRunArtifact = {
  testRunId: string;
  datasetId: string | null;
  flowVersionId: string | null;
  runStatus: string | null;
  progress: TestRunVersionProgress | null;
  /** True when the runner gave up waiting and the run may still be going. */
  handoff: boolean;
};

type UnknownRecord = Record<string, unknown>;

const asRecord = (value: unknown): UnknownRecord | null =>
  typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as UnknownRecord)
    : null;

const readString = (source: UnknownRecord, key: string): string | null => {
  const value = source[key];
  return typeof value === "string" && value.length > 0 ? value : null;
};

const readNumber = (source: UnknownRecord, key: string): number | null => {
  const value = source[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
};

/**
 * Reads run counts into the shape the test-run progress UI already speaks.
 *
 * Two layouts reach here. The runner nests counts under `progress` for the
 * call it waits inside, omitting any the run API has not reported yet — so
 * that object can be empty. Every other tool's artifact is its raw structured
 * content, where the same counts sit flat. Without a positive `row_count`
 * there is nothing to divide by, so callers get `null` and fall back to a
 * placeholder bar.
 */
const readProgress = (source: UnknownRecord): TestRunVersionProgress | null => {
  const counts = asRecord(source.progress) ?? source;
  const total = readNumber(counts, "row_count");
  if (total === null || total <= 0) {
    return null;
  }
  return {
    total,
    successful: readNumber(counts, "succeeded") ?? 0,
    failed: readNumber(counts, "failed") ?? 0,
    ignored: readNumber(counts, "ignored") ?? 0,
    output_mismatch: readNumber(counts, "mismatched") ?? 0,
  };
};

export const parsePolledRun = (
  liveStatusDetail: LiveStatusDetail | null | undefined,
  now: number = Date.now(),
): PolledRun | null => {
  if (liveStatusDetail?.kind !== LiveStatusDetailKind.POLLING_FOR_RESULT) {
    return null;
  }
  // The API filters expired entries on read, but the schema asks clients to
  // stale-check too — a runner that stopped writing leaves the last entry
  // behind until the session transitions.
  const until = Date.parse(liveStatusDetail.until);
  if (!Number.isNaN(until) && until <= now) {
    return null;
  }
  // The live status carries the same artifact shape as the tool result.
  const artifact = asRecord(liveStatusDetail.artifact);
  const testRunId = artifact && readString(artifact, "test_run_id");
  if (!artifact || testRunId === null) {
    return null;
  }
  return {
    testRunId,
    datasetId: readString(artifact, "dataset_id"),
    flowVersionId: readString(artifact, "flow_version_id"),
    progress: readProgress(artifact),
  };
};

/**
 * Run statuses the runner treats as terminal. Anything else — `running`, or a
 * status this bundle does not know — means the run is still going.
 */
const SETTLED_RUN_STATUSES: ReadonlySet<string> = new Set([
  "finished",
  "failed",
]);

/**
 * Whether an artifact reports a run that is over.
 *
 * Only a settled run can be rendered from the artifact alone. Every MCP tool
 * result carries its structured content as an artifact, so the call that
 * *starts* a run reports it too — with `run_status: "running"` and no counts.
 * Reading that as a result showed a finished card seconds after the run
 * began; a handoff is likewise a run still in flight.
 */
export const isSettledRun = (artifact: TestRunArtifact): boolean =>
  !artifact.handoff &&
  artifact.runStatus !== null &&
  SETTLED_RUN_STATUSES.has(artifact.runStatus);

export const parseTestRunArtifact = (
  toolResult: ToolMessage | null | undefined,
): TestRunArtifact | null => {
  const artifact = asRecord(toolResult?.artifact);
  const testRunId = artifact && readString(artifact, "test_run_id");
  if (!artifact || testRunId === null) {
    return null;
  }
  return {
    testRunId,
    datasetId: readString(artifact, "dataset_id"),
    flowVersionId: readString(artifact, "flow_version_id"),
    runStatus: readString(artifact, "run_status"),
    progress: readProgress(artifact),
    handoff: artifact.handoff === true,
  };
};
